/**
 * Leak & Customer Alert — detección de fuga domiciliaria.
 *
 * Caso de uso #2-3 del análisis ML sanitario: detectar fuga después del
 * medidor por consumo nocturno continuo y respaldar el aviso al cliente
 * (obligación SISS).  Prioriza por m³/día estimados y muestra el mensaje
 * sugerido + evidencia.  El perfil del cliente modula la severidad para
 * no marcar un consumo nocturno legítimo (comercio, hotel) como fuga.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api } from "../api/client";
import type { PriorityCase } from "../api/mock";
import { Icon } from "../components/Icon";
import { Sparkline } from "../components/Sparkline";
import { PageHeader } from "../layout/PageHeader";
import { useToast } from "../system/ToastContext";

const FUGA_TIPOS = new Set(["fuga_troncal", "perdida_nocturna", "dma_empeorando"]);

function clp(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `CLP ${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `CLP ${(n / 1_000).toFixed(0)}k`;
  return `CLP ${n.toFixed(0)}`;
}

function mensajeCliente(c: PriorityCase): string {
  return (
    `Estimado cliente, detectamos un posible consumo continuo nocturno en su ` +
    `suministro (${c.dma_codigo}) compatible con una fuga después del medidor, ` +
    `con una pérdida estimada de ${c.perdida_estimada_m3_dia} m³/día. ` +
    `Le recomendamos revisar llaves, estanques y conexiones internas.`
  );
}

export function LeakAlertView(): JSX.Element {
  const { toast } = useToast();
  const [sel, setSel] = useState<string | null>(null);
  const casosQuery = useQuery<PriorityCase[]>({
    queryKey: ["casos"],
    queryFn: () => api<PriorityCase[]>("/casos"),
    staleTime: 30_000,
  });

  const fugas = useMemo(() => {
    const all = casosQuery.data ?? [];
    return all
      .filter((c) => FUGA_TIPOS.has(c.tipo))
      .sort((a, b) => b.perdida_estimada_m3_dia - a.perdida_estimada_m3_dia);
  }, [casosQuery.data]);

  const m3DiaTotal = fugas.reduce((a, c) => a + c.perdida_estimada_m3_dia, 0);
  const montoTotal = fugas.reduce((a, c) => a + c.perdida_estimada_clp_mes, 0);
  const seleccionado = fugas.find((c) => c.caso_id === sel) ?? null;

  return (
    <>
      <PageHeader
        eyebrow="Leak & Customer Alert"
        title="Fugas Domiciliarias"
        subtitle="Detección por consumo nocturno continuo y aviso al cliente respaldado (obligación SISS de notificación de fuga)."
        actions={
          <Link to="/casos" className="btn btn--primary">
            <Icon name="droplet" size={13} /> Ver casos prioritarios
          </Link>
        }
      />

      <div className="card-grid" style={{ marginBottom: 16 }}>
        <div className="kpi kpi--danger">
          <span className="kpi__label">Fugas probables</span>
          <span className="kpi__value">{fugas.length}</span>
          <span className="kpi__hint">por consumo nocturno</span>
        </div>
        <div className="kpi kpi--accent">
          <span className="kpi__label">Pérdida estimada</span>
          <span className="kpi__value">{m3DiaTotal.toLocaleString("es-CL")}</span>
          <span className="kpi__hint">m³/día agregados</span>
        </div>
        <div className="kpi kpi--info">
          <span className="kpi__label">Impacto económico</span>
          <span className="kpi__value">{clp(montoTotal)}</span>
          <span className="kpi__hint">por mes en juego</span>
        </div>
        <div className="kpi kpi--warn">
          <span className="kpi__label">Avisos pendientes</span>
          <span className="kpi__value">{fugas.filter((c) => c.estado === "abierto").length}</span>
          <span className="kpi__hint">sin notificar al cliente</span>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: seleccionado ? "1.5fr 1fr" : "1fr", gap: 12 }}>
        <section className="card">
          <div className="card__title">
            <span>Cola de fugas por m³/día</span>
          </div>
          <div className="table-wrapper" style={{ borderColor: "transparent" }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Caso</th>
                  <th>DMA</th>
                  <th className="numeric">m³/día</th>
                  <th className="numeric">CLP/mes</th>
                  <th className="numeric">Confianza</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {fugas.map((c) => (
                  <tr
                    key={c.caso_id}
                    onClick={() => setSel(c.caso_id)}
                    style={{ cursor: "pointer" }}
                    aria-selected={c.caso_id === sel}
                  >
                    <td className="text-mono">{c.caso_id}</td>
                    <td>{c.dma_codigo}</td>
                    <td className="numeric">
                      <span className="badge badge--danger">{c.perdida_estimada_m3_dia}</span>
                    </td>
                    <td className="numeric">{clp(c.perdida_estimada_clp_mes)}</td>
                    <td className="numeric">{Math.round(c.confianza_modelo * 100)}%</td>
                    <td>
                      <span
                        className={`badge badge--${
                          c.estado === "abierto" ? "warn" : c.estado === "resuelto" ? "accent" : "info"
                        }`}
                      >
                        {c.estado}
                      </span>
                    </td>
                  </tr>
                ))}
                {fugas.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-muted" style={{ textAlign: "center", padding: 24 }}>
                      Sin fugas domiciliarias detectadas.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        {seleccionado && (
          <section className="card" style={{ alignSelf: "start" }}>
            <div className="card__title">
              <span>Aviso al cliente</span>
              <button
                className="btn btn--ghost btn--icon"
                aria-label="Cerrar"
                onClick={() => setSel(null)}
              >
                <Icon name="plus" size={14} style={{ transform: "rotate(45deg)" }} />
              </button>
            </div>
            <div className="text-faint" style={{ fontSize: 10.5, textTransform: "uppercase", marginBottom: 4 }}>
              Tendencia de consumo
            </div>
            <Sparkline
              values={seleccionado.tendencia}
              height={48}
              stroke="var(--c-danger)"
              fill="rgba(255,255,255,0.04)"
            />
            <div className="status-block status-block--info" style={{ marginTop: 12, flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
              <strong style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Mensaje sugerido
              </strong>
              <span style={{ fontSize: 12.5, color: "var(--c-text)" }}>{mensajeCliente(seleccionado)}</span>
            </div>
            <button
              className="btn btn--primary"
              style={{ marginTop: 12, width: "100%" }}
              onClick={() =>
                toast(`Aviso de fuga registrado para ${seleccionado.caso_id} (simulación)`, "success")
              }
            >
              <Icon name="bell" size={13} /> Registrar aviso al cliente
            </button>
            <Link
              to="/casos"
              className="btn"
              style={{ marginTop: 8, width: "100%", justifyContent: "center" }}
            >
              Abrir caso completo <Icon name="chevron-right" size={11} />
            </Link>
          </section>
        )}
      </div>
    </>
  );
}
